import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Search } from 'lucide-react';
import { api } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';

/**
 * Who gets which texts (owner, Oct 6 2026: "select which texts who gets, like
 * billing and scheduling updates"). One row per customer, one switch per kind.
 * Everything starts on; a switched-off kind is never texted to that customer,
 * whichever screen or timer would send it (server/services/textPrefs.js).
 * A customer who replied STOP gets nothing and shows as such.
 */
type Kind = { key: string; label: string; help: string };
type Row = { id: number; name: string; phone: string | null; sms_opt_out: boolean; is_regular: boolean; off: string[]; extra_numbers?: number };

export default function TextPrefs() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data, isLoading } = useQuery<{ kinds: Kind[]; customers: Row[] }>('text-prefs', () => api.get('/customers/texts/grid').then(r => r.data));
  const [q, setQ] = useState('');
  const [onlyRegular, setOnlyRegular] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const kinds = data?.kinds || [];

  const rows = useMemo(() => {
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const digits = q.replace(/\D/g, '');
    return (data?.customers || []).filter(c => {
      if (onlyRegular && !c.is_regular && !q) return false;
      if (!words.length) return true;
      const hay = c.name.toLowerCase();
      return words.every(w => hay.includes(w)) || (digits.length >= 3 && String(c.phone || '').replace(/\D/g, '').includes(digits));
    });
  }, [data, q, onlyRegular]);

  const setLocal = (id: number, off: string[]) => qc.setQueryData<any>('text-prefs', (d: any) => d && ({ ...d, customers: d.customers.map((c: Row) => c.id === id ? { ...c, off } : c) }));

  async function toggle(c: Row, kind: string) {
    const off = c.off.includes(kind) ? c.off.filter(k => k !== kind) : c.off.concat(kind);
    setLocal(c.id, off);
    try { await api.put(`/customers/${c.id}/texts`, { off }); }
    catch (e: any) { setLocal(c.id, c.off); toast.error(e?.response?.data?.message || 'Could not save that'); }
  }

  async function column(k: Kind, on: boolean) {
    const ids = rows.filter(c => !c.sms_opt_out).map(c => c.id);
    if (!ids.length) return;
    if (!window.confirm(`Turn "${k.label}" texts ${on ? 'ON' : 'OFF'} for the ${ids.length} customer${ids.length === 1 ? '' : 's'} shown?`)) return;
    setBusy(k.key);
    try {
      const r = await api.post('/customers/texts/bulk', { kind: k.key, on, ids });
      toast.success(r.data.message);
      qc.invalidateQueries('text-prefs');
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not save that'); }
    finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/customers" className="text-sm text-gray-500 inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Customers</Link>
        <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900 mt-1">Who gets which texts</h1>
        <p className="text-sm text-gray-600 mt-1">Untick a box and that customer stops getting that kind of text, from every screen and every automatic send. Everything starts on. Texts you type yourself on Messages still go.</p>
        <p className="text-sm text-gray-600 mt-1">A customer with more than one number or email: which one gets what is set on their card (Customers → open them → Who gets which texts and emails). A box here ticked means at least one of their numbers gets it; emails have their own boxes on the card.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or phone" className="input pl-9 w-full" />
        </div>
        <label className="text-sm text-gray-700 flex items-center gap-2">
          <input type="checkbox" checked={onlyRegular} onChange={e => setOnlyRegular(e.target.checked)} className="rounded text-brand-600" />
          Only regular customers
        </label>
      </div>

      {isLoading ? <p className="text-sm text-gray-500">Loading…</p> : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-gray-700 sticky left-0 bg-gray-50">Customer</th>
                {kinds.map(k => (
                  <th key={k.key} className="px-2 py-2 text-center font-semibold text-gray-700 align-bottom" title={k.help}>
                    <div className="leading-tight">{k.label}</div>
                    {!readOnly && (
                      <div className="text-[11px] font-normal text-gray-500 mt-1 whitespace-nowrap">
                        <button disabled={!!busy} onClick={() => column(k, true)} className="underline">all on</button>{' · '}
                        <button disabled={!!busy} onClick={() => column(k, false)} className="underline">all off</button>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 sticky left-0 bg-white">
                    <Link to={`/customers/${c.id}`} className="font-medium text-gray-900 hover:underline">{c.name}</Link>
                    <div className="text-xs text-gray-500">
                      {c.sms_opt_out ? <span className="text-red-700">Replied STOP — gets no texts until they text START</span> : c.phone || <span className="text-amber-700">no phone</span>}
                      {(c.extra_numbers || 0) > 0 && <Link to={`/customers?profile=${c.id}`} className="ml-1.5 text-brand-700 hover:underline">+{c.extra_numbers} number{c.extra_numbers === 1 ? '' : 's'}</Link>}
                    </div>
                  </td>
                  {kinds.map(k => (
                    <td key={k.key} className="px-2 py-2 text-center">
                      <input type="checkbox" aria-label={`${k.label} texts for ${c.name}`}
                        checked={!c.sms_opt_out && !c.off.includes(k.key)}
                        disabled={readOnly || c.sms_opt_out}
                        onChange={() => toggle(c, k.key)}
                        className="h-4 w-4 rounded text-brand-600 disabled:opacity-40" />
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={kinds.length + 1} className="px-3 py-6 text-center text-gray-500">{q ? `No customers match "${q}"` : 'No customers'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-xs text-gray-500 space-y-0.5">
        {kinds.map(k => <p key={k.key}><b>{k.label}:</b> {k.help}</p>)}
      </div>
    </div>
  );
}
